import React, { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  ReferenceLine, Legend,
} from "recharts";
import { Plus, AlertCircle, Loader2 } from "lucide-react";
import { fmtDate, Field, BPCategoryBadge } from "./shared";

export default function BPTab({ data, onSaved }) {
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    systolic: "",
    diastolic: "",
    heart_rate: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const chartData = useMemo(() => (
    [...data.readings]
      .slice()
      .reverse()
      .map((r) => ({
        date: fmtDate(r.date),
        systolic: r.systolic,
        diastolic: r.diastolic,
      }))
  ), [data.readings]);

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (!form.systolic || !form.diastolic) {
      setError("Systolic and diastolic are both required.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/specialties/cardiology/bp", {
        date: form.date,
        systolic: Number(form.systolic),
        diastolic: Number(form.diastolic),
        heart_rate: form.heart_rate ? Number(form.heart_rate) : null,
        notes: form.notes || null,
      });
      toast.success("BP reading logged");
      setForm((f) => ({ ...f, systolic: "", diastolic: "", heart_rate: "", notes: "" }));
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to save reading");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6" data-testid="cardio-bp-tab">
      <form onSubmit={submit} className="ros-card space-y-4" data-testid="bp-form">
        <div className="font-heading text-lg">Log a new reading</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Field label="Date" testId="bp-date">
            <input type="date" value={form.date}
                   onChange={(e) => setForm({ ...form, date: e.target.value })}
                   data-testid="bp-input-date"
                   className="ros-input" required />
          </Field>
          <Field label="Systolic (mmHg)" testId="bp-systolic">
            <input type="number" min={40} max={260} value={form.systolic}
                   onChange={(e) => setForm({ ...form, systolic: e.target.value })}
                   data-testid="bp-input-systolic"
                   placeholder="128" className="ros-input" required />
          </Field>
          <Field label="Diastolic (mmHg)" testId="bp-diastolic">
            <input type="number" min={30} max={180} value={form.diastolic}
                   onChange={(e) => setForm({ ...form, diastolic: e.target.value })}
                   data-testid="bp-input-diastolic"
                   placeholder="82" className="ros-input" required />
          </Field>
          <Field label="Heart rate (bpm)" testId="bp-hr">
            <input type="number" min={30} max={220} value={form.heart_rate}
                   onChange={(e) => setForm({ ...form, heart_rate: e.target.value })}
                   data-testid="bp-input-hr"
                   placeholder="72" className="ros-input" />
          </Field>
          <Field label="Notes" testId="bp-notes">
            <input type="text" value={form.notes}
                   onChange={(e) => setForm({ ...form, notes: e.target.value })}
                   data-testid="bp-input-notes"
                   placeholder="Morning, resting" className="ros-input" />
          </Field>
        </div>
        {error && (
          <div className="text-sm text-[#F87171] flex items-center gap-2" data-testid="bp-error">
            <AlertCircle size={14} /> {error}
          </div>
        )}
        <div>
          <button type="submit" disabled={saving}
                  data-testid="bp-submit"
                  className="flex items-center gap-2 px-5 py-2 rounded-full bg-[#F87171] text-black font-semibold hover:bg-[#EF4444] disabled:opacity-50 transition-colors">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            {saving ? "Saving…" : "Log reading"}
          </button>
        </div>
      </form>

      {chartData.length > 0 && (
        <div className="ros-card" data-testid="bp-chart">
          <div className="font-heading text-lg mb-3">BP trend</div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 10, right: 16, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#272E2B" />
                <XAxis dataKey="date" stroke="#A3ACA7" fontSize={11} />
                <YAxis stroke="#A3ACA7" fontSize={11} domain={[50, 200]} />
                <Tooltip contentStyle={{ background: "#0F1312", border: "1px solid #272E2B", borderRadius: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <ReferenceLine y={130} stroke="#FBBF24" strokeDasharray="4 4" label={{ value: "Sys target 130", fontSize: 10, fill: "#FBBF24" }} />
                <ReferenceLine y={80}  stroke="#FBBF24" strokeDasharray="4 4" label={{ value: "Dia target 80",  fontSize: 10, fill: "#FBBF24" }} />
                <Line type="monotone" dataKey="systolic"  stroke="#F87171" strokeWidth={2} dot={{ r: 3 }} name="Systolic" />
                <Line type="monotone" dataKey="diastolic" stroke="#60A5FA" strokeWidth={2} dot={{ r: 3 }} name="Diastolic" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="ros-card" data-testid="bp-history">
        <div className="font-heading text-lg mb-3">History ({data.readings.length})</div>
        {data.readings.length === 0 ? (
          <div className="text-sm text-[#A3ACA7]">No readings yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] text-[#A3ACA7] uppercase tracking-wider">
                  <th className="py-2 pr-4">Date</th>
                  <th className="py-2 pr-4">BP</th>
                  <th className="py-2 pr-4">HR</th>
                  <th className="py-2 pr-4">Category</th>
                  <th className="py-2">Notes</th>
                </tr>
              </thead>
              <tbody>
                {data.readings.map((r) => (
                  <tr key={r.id} className="border-t border-[#272E2B]" data-testid={`bp-row-${r.id}`}>
                    <td className="py-2 pr-4 text-white">{fmtDate(r.date)}</td>
                    <td className="py-2 pr-4 text-white font-mono">{r.systolic}/{r.diastolic}</td>
                    <td className="py-2 pr-4 text-[#A3ACA7]">{r.heart_rate || "—"}</td>
                    <td className="py-2 pr-4"><BPCategoryBadge category={r.category} /></td>
                    <td className="py-2 text-[#A3ACA7]">{r.notes || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
